/**
 * Shared asset-path surface for the export pipeline.
 *
 * The scene-document EXPORTER (`.tracex` / folder / standalone `.gltf`) no longer
 * uses the field allowlist below — it
 * discovers assets structurally (recurse all values; collect local `assets/` paths and
 * `src`-named values), matching the `.trace` exporter so the two formats can't diverge on
 * which fields it inspects. See `tracex-exporter._collectAssetPathsDeep`.
 *
 * The publish-time VALIDATOR (`validateSrcRefAssets`) still uses `BEHAVIOR_ASSET_FIELDS` /
 * `BEHAVIOR_NESTED_ASSET_FIELDS`: it has the opposite job — flag the specific fields that
 * MUST already be hosted URLs at publish. That set is intentionally narrower than "every
 * asset path": e.g. a ParticleSystem's baked `texture` is a local `assets/…` path at
 * authoring time (bundled into `.zip` / uploaded on publish), so it must NOT be in this
 * list or publishing any custom-textured particle scene would be rejected.
 */

// Re-exported from the shared util so existing `export/` importers keep working
// while the definition lives in one neutral place (utils/assetPaths).
export { isLocalAssetPath } from '../utils/assetPaths';

/**
 * Top-level behavior fields the publish validator requires to be hosted URLs.
 * NOT used by the exporter (which walks structurally). Adding a field here makes
 * the validator reject scenes whose value isn't an `http(s)://` URL — only add
 * fields that genuinely must be resolved to a hosted URL before publish.
 */
export const BEHAVIOR_ASSET_FIELDS = [
    // Provenance's `bakedFrom` names the authored asset a baked `src` replaced.
    // It IS fetched — an authoring load resolves it instead of the bake — so it
    // has to clear the same bar as any other ref, or a published file carries a
    // way back that points at nothing. (`ProvenanceAsset.ref` beside it does
    // NOT belong here: that one is inert generation history. Actual node
    // dependencies are validated on the nodes that carry them.)
    'bakedFrom',
    'meshPath', 'videoPath', 'imagePath', 'texturePath',
    'modelPath', 'audioPath', 'src',
    // Do NOT add ParticleSystem's `texture` here. It is a local `assets/…` path at authoring
    // time (bundled into `.zip` / uploaded on publish), not a pre-hosted URL — validating it
    // would reject publishing every custom-textured particle scene. The exporter already
    // collects it via the structural walk; this list is publish-URL validation only.
    //
    // `Properties.sheets` is NOT here either, for a shape reason rather than a policy one:
    // it must be a hosted URL at publish, but it is a string ARRAY and this list is read as
    // `obj[field]` expecting one string. It is validated in the validator's Properties
    // branch (`collectSheetOffenders`), which reports each entry by index.
] as const;

/**
 * Nested asset-path slots on common sub-objects checked by the validator. Each
 * entry says "if the behavior has an object at `parent`, check `key`".
 */
export const BEHAVIOR_NESTED_ASSET_FIELDS: ReadonlyArray<{ parent: 'texture' | 'material'; key: string }> = [
    { parent: 'texture', key: 'path' },
    { parent: 'texture', key: 'src' },
    { parent: 'material', key: 'texturePath' },
];

/**
 * Recursion cap for nested asset-path discovery. Shared so the exporter's
 * structural walk and the validator's `src` recursion have the same reach.
 */
export const NESTED_SRC_RECURSION_DEPTH = 5;

/** Generation history is carried verbatim, not fetched, validated as media or
 * rewritten. bakedFrom remains a live dependency and is deliberately excluded. */
export function isProvenanceHistoryField(record: Record<string, unknown>, key: string): boolean {
    return record['@type'] === 'Provenance' && (key === 'decisions' || key === 'assets');
}
